import { useState, useRef, useCallback } from 'react';
import { Toast } from 'antd-mobile';

interface UseVoiceRecognizerProps {
  onTextRecognized?: (text: string) => void;
  onAudioLevel?: (level: number) => void;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  language?: string; // 场景语言代码
  sceneId?: string; // 场景ID，用于记录使用情况
}

interface AudioInfo {
  sampleRate: number;
  channelCount: number;
}

const useVoiceRecognizer = ({
  onTextRecognized,
  onAudioLevel,
  onStartRecording,
  onStopRecording,
  language = 'zh-CN', // 默认中文
  sceneId // 场景ID
}: UseVoiceRecognizerProps) => {
  // 基本状态
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [recognizedText, setRecognizedText] = useState<string>('');
  const [audioInfo, setAudioInfo] = useState<AudioInfo | null>(null);

  // 引用
  const websocketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioLevelIntervalRef = useRef<number | null>(null);

  // 移除硬编码的API配置，改为使用JWT认证

  // 获取JWT Token
  const getAuthToken = (): string | null => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  };

  // 连接WebSocket
  const connectWebSocket = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const jwt_token = getAuthToken();
      if (!jwt_token) {
        reject(new Error('未找到认证token'));
        return;
      }

      const wsUrl = (process.env.REACT_APP_WS_BASE_URL || 'ws://localhost:8000') + `/api/v1/agent/ws/voice-stream?token=${jwt_token}`;
      websocketRef.current = new WebSocket(wsUrl);

      websocketRef.current.onopen = () => {
        console.log('WebSocket连接已建立');
        
        // 发送语音认证
        websocketRef.current?.send(JSON.stringify({
          type: 'voice_auth',
          data: {
            token: jwt_token,
            language: language, // 传递场景语言
            scene_id: sceneId // 传递场景ID，用于记录使用情况
          }
        }));
        
        resolve();
      };

      websocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('收到响应:', data);

          if (data.type === 'voice_transcription') {
            const text = data.data?.text || '';
            setRecognizedText(text);
            if (onTextRecognized) onTextRecognized(text);
          }
        } catch (error) {
          console.error('解析WebSocket消息失败:', error);
        }
      };

      websocketRef.current.onerror = (error) => {
        console.error('WebSocket连接错误:', error);
        reject(error);
      };

      websocketRef.current.onclose = (event) => {
        console.log('WebSocket连接关闭:', event.code, event.reason);
      };
    });
  };

  // 将Float32Array转换为16位PCM
  const float32ToPCM16 = (float32Array: Float32Array): Uint8Array => {
    const pcm16 = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const sample = Math.max(-1, Math.min(1, float32Array[i]));
      pcm16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    }
    return new Uint8Array(pcm16.buffer);
  };

  // 发送音频数据
  const sendAudioData = (pcmData: Uint8Array): void => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      websocketRef.current.send(JSON.stringify({
        type: 'voice_audio',
        data: Array.from(pcmData),
        format: 'pcm',
        sample_rate: 16000,
        channels: 1,
        bit_depth: 16
      }));
    }
  };

  // 设置音频电平分析
  const setupAudioLevelAnalysis = useCallback(() => {
    if (!analyserRef.current || !onAudioLevel) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const updateAudioLevel = () => {
      if (!analyserRef.current || !isRecording) return;
      
      analyserRef.current.getByteFrequencyData(dataArray);
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const average = sum / bufferLength;
      const normalizedLevel = average / 255;
      
      onAudioLevel(normalizedLevel);
    };

    audioLevelIntervalRef.current = window.setInterval(updateAudioLevel, 100);
  }, [isRecording, onAudioLevel]);

  // 开始录音
  const startRecording = async (): Promise<void> => {
    try {
      console.log('开始录音...');
      setIsConnecting(true);

      // 1. 获取麦克风权限
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // 2. 连接WebSocket
      await connectWebSocket();

      // 3. 创建音频处理
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 16000
      });

      sourceRef.current = audioContextRef.current.createMediaStreamSource(streamRef.current);
      analyserRef.current = audioContextRef.current.createAnalyser();
      processorRef.current = audioContextRef.current.createScriptProcessor(4096, 1, 1);

      // 4. 设置音频处理
      processorRef.current.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0);
        const pcmData = float32ToPCM16(inputData);
        sendAudioData(pcmData);
      };

      // 5. 连接音频节点
      sourceRef.current.connect(analyserRef.current);
      sourceRef.current.connect(processorRef.current);
      
      // 创建静音终点避免回声
      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      processorRef.current.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      // 6. 设置音频电平分析
      if (onAudioLevel) {
        setupAudioLevelAnalysis();
      }

      // 7. 更新状态
      setAudioInfo({
        sampleRate: audioContextRef.current.sampleRate,
        channelCount: 1
      });

      setIsRecording(true);
      setIsConnecting(false);
      
      console.log('录音已开始');
      if (onStartRecording) onStartRecording();

    } catch (error) {
      console.error('录音失败:', error);
      setIsConnecting(false);
      // Toast.show('录音失败: ' + (error as Error).message);
      if ((error as Error).message.includes('Permission denied')) {
        Toast.show('请授权麦克风权限');
      }
      
      // 清理资源
      await cleanup();
    }
  };

  // 停止录音
  const stopRecording = async (): Promise<void> => {
    try {
      console.log('停止录音...');

      // 1. 发送结束信号
      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        websocketRef.current.send(JSON.stringify({
          type: 'voice_end'
        }));
      }

      // 2. 清理所有资源
      await cleanup();

      // 3. 更新状态
      setIsRecording(false);
      setRecognizedText('');
      setAudioInfo(null);

      console.log('录音已停止');
      if (onStopRecording) onStopRecording();

    } catch (error) {
      console.error('停止录音失败:', error);
    }
  };

  // 清理资源
  const cleanup = async (): Promise<void> => {
    // 清理音频电平分析
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }

    // 清理音频处理节点
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current = null;
    }

    // 关闭音频上下文
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // 停止音频流
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // 关闭WebSocket
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
  };

  return {
    isRecording,
    isConnecting,
    recognizedText,
    audioInfo,
    startRecording,
    stopRecording
  };
};

export default useVoiceRecognizer;